import { WebPlanTier } from '../../constants/allowance';
import {
  AttachmentImage,
  BalanceJson,
  CarouselItem,
  I18n,
  UserId,
  Uuid,
} from '../../models/Base';
import { RepositoryElement } from '../../models/Repository';
import {
  AlbumRankingType,
  AlbumRankTier,
  SpaceBalance,
  SubscriptionType,
} from '../../models/SpaceStore';
import { deepTransformDate, deepTransformTimestamp } from '../../utils/date';
import { MoneyJson, ReferralMoneyJson } from '../UserStore';

import { RewardPlaform } from './RewardCategory';
import { ReferralSystem, RewardReferralRole } from './RewardReferral';

export type RewardOfferInfo = {
  name: string;
  description: string | null;
  shortDescription: string | null;
  carousel: CarouselItem[];
};
type I18nRewardOfferInfo = I18n<RewardOfferInfo>;

export enum Provider {
  Joyajoy = 'joyajoy',
  Wea = 'wea',
  Wcb = 'wcb',
}

export enum RewardOfferType {
  Diamond = 'diamond',
  Token = 'token',
  Bread = 'bread',
  AvatarItem = 'avatarItem',
  SubscriptionKey = 'subscriptionKey',
  Coupon = 'coupon',
  GiftCard = 'giftCard',
  PhysicalGood = 'physicalGood',
  Certificate = 'certificate',
  ReferralRole = 'referralRole',
  MembershipTier = 'classroomMembershipTier',
  ClassroomMembershipTierSet = 'classroomMembershipTierSet',
  ClassroomPackage = 'classroomPackage',
  WebPlan = 'webPlan',
  WeaMembership = 'weaMembership',
}

interface Diamond {
  type: RewardOfferType.Diamond;
  quantity: number;
}

interface Token {
  type: RewardOfferType.Token;
  quantity: number;
}

interface Bread {
  type: RewardOfferType.Bread;
  quantity: number;
}

interface AvatarItem {
  type: RewardOfferType.AvatarItem;
  itemId: string;
}

interface SubscriptionKey<T = number> {
  type: RewardOfferType.SubscriptionKey;
  subscriptionKey: Uuid;
  classroomId?: Uuid;
  expiredAt?: T;
  validityPeriod?: number;
}

type I18nRewardInfo = I18n<{
  name: string;
  description: string | null;
  terms: string[];
}>;

export enum Currency {
  USD = 'USD',
  PHP = 'PHP',
  HKD = 'HKD',
  THB = 'THB',
  CNY = 'CNY',
}

interface Coupon<T = number> {
  type: RewardOfferType.Coupon;
  i18nCouponInfo: I18nRewardInfo;
  expiredAt?: T;
  validityPeriod?: number;
  currency?: Currency; // default value USD
  minSpend?: number; // default value 0
  discountPercentage?: number;
  discountValue?: number;
}

export enum GiftCardType {
  Cash = 'cash',
  Credit = 'credit',
}

interface GiftCard<T = number> {
  type: RewardOfferType.GiftCard;
  i18nGiftCardInfo: I18nRewardInfo;
  initialAmount: number;
  expiredAt?: T;
  validityPeriod?: number;
  giftCardType?: GiftCardType; // default value cash
  currency?: Currency; // default value USD
  maxTransactions?: number | null;
}

interface PhysicalGood {
  type: RewardOfferType.PhysicalGood;
  // itemId: string,
}

type Cerificate = {
  type: RewardOfferType.Certificate;
  classroomId: Uuid;
  certificationName: string; //gameSetsLeaderboard.[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}.(?:individual|team|region).(?:band(?:1|2|3))
};

type ReferralRole = {
  type: RewardOfferType.ReferralRole;
  role: RewardReferralRole.Partner | RewardReferralRole.Distributor;
};

type MembershipTier = {
  type: RewardOfferType.MembershipTier;
  classroomId: Uuid;
  tierId: Uuid;
};

type ClassroomMembershipTierSet = {
  type: RewardOfferType.ClassroomMembershipTierSet;
  link?: string;
  i18nVariantsData?: I18nVariantsData;
  items: MembershipTierItem[];
};

type ClassroomPackageItem = {
  classroomId: Uuid;
  tierId: Uuid;
  subscriptionType:
    | SubscriptionType.Monthly
    | SubscriptionType.Yearly
    | SubscriptionType.Permanent;
};

type ClassroomPackage = {
  type: RewardOfferType.ClassroomPackage;
  creationToken?: number;
  hasAIRevision?: boolean;
  items?: ClassroomPackageItem[];
};

type WebPlanItem = {
  variant: {
    tier: WebPlanTier;
    period: SubscriptionType.Monthly | SubscriptionType.Yearly;
  };
  price?: RewardOfferPrice;
};

type WebPlan = {
  type: RewardOfferType.WebPlan;
  link?: string;
  items: WebPlanItem[];
};

type WeaMembership = {
  type: RewardOfferType.WeaMembership;
};

type MembershipTierItem = {
  variant: { [key: string]: string };
  classroomId: Uuid;
  tierId: Uuid;
  subscriptionType:
    | SubscriptionType.Monthly
    | SubscriptionType.Yearly
    | SubscriptionType.Permanent;
  price?: RewardOfferPrice;
};

export type VariantsData = {
  key: string;
  groupName: string;
  options: VariantOption[];
};

type I18nVariantsData = I18n<VariantsData[]>;

type VariantOption = {
  value: string;
  name: string;
  description: string | null;
  icon?: AttachmentImage;
};

export type RewardOfferReward<T = number> =
  | Diamond
  | Token
  | Bread
  | AvatarItem
  | SubscriptionKey<T>
  | Coupon<T>
  | GiftCard<T>
  | PhysicalGood
  | Cerificate
  | MembershipTier
  | ReferralRole
  | ClassroomMembershipTierSet
  | ClassroomPackage
  | WebPlan
  | WeaMembership;

export interface RewardOfferPrice
  extends BalanceJson,
    SpaceBalance,
    MoneyJson,
    ReferralMoneyJson {}

export enum DeliveryMethodType {
  InApp = 'In-app',
  Email = 'email',
  Mail = 'mail',
}

export enum OfferStateType {
  Active = 'active',
  Scheduled = 'scheduled',
  Deactive = 'deactive',
}

type RewardOfferRequirementLeaderboard = {
  leaderboardId: Uuid;
  rankTier: AlbumRankTier;
  rankingType: AlbumRankingType;
};
export type RewardOfferRequirement =
  | {
      classroomSummary: {
        scoreTotal: number;
      };
    }
  | {
      leaderboard: RewardOfferRequirementLeaderboard;
    };

enum Audience {
  Public = 'public',
  Followers = 'followers',
  Members = 'members',
}

export interface RewardOfferJson<T = number> {
  readonly rewardOfferId: Uuid;
  store: RewardPlaform;
  createdBy: UserId;
  createdAt: T;
  firstStartedAt: T;
  startedAt: T;
  endedAt?: T;
  i18nRewardOfferInfo: I18nRewardOfferInfo;
  rewardCategoryId: Uuid | null;
  regions: string[]; // RegionCode[];?
  provider: Provider | null;
  reward: RewardOfferReward<T>;
  price: RewardOfferPrice;
  quota: number;
  maxRedemptionsPerUser: number | null;
  redemptionCount: number;
  requirement: RewardOfferRequirement | null;
  audience: Audience | [Uuid] | null;
  viewOnlyAudience: Audience | [Uuid] | null;
  referralSystem: ReferralSystem | null;
}

export class RewardOffer
  extends RepositoryElement
  implements RewardOfferJson<Date>
{
  readonly rewardOfferId!: Uuid;
  store!: RewardPlaform;
  createdBy!: UserId;
  createdAt!: Date;
  firstStartedAt!: Date;
  startedAt!: Date;
  endedAt?: Date;
  i18nRewardOfferInfo!: I18nRewardOfferInfo;
  rewardCategoryId!: Uuid | null;
  regions!: string[]; // RegionCode[];?
  provider!: Provider | null;
  reward!: RewardOfferReward<Date>;
  price!: RewardOfferPrice;
  quota!: number;
  maxRedemptionsPerUser!: number | null;
  redemptionCount!: number;
  requirement!: RewardOfferRequirement | null;
  audience!: Audience | [Uuid] | null;
  viewOnlyAudience!: Audience | [Uuid] | null;
  referralSystem!: ReferralSystem | null;

  constructor(rewardOffer: RewardOfferJson<Date | number>) {
    super();
    Object.assign(this, deepTransformTimestamp(rewardOffer));
  }

  id(): string {
    return this.rewardOfferId;
  }

  toJSON(): RewardOfferJson {
    return deepTransformDate(this) as unknown as RewardOfferJson;
  }

  get remainingQuota() {
    return this.quota - this.redemptionCount;
  }

  get isOutOfStock(): boolean {
    return this.remainingQuota <= 0;
  }

  get deliveryMethod() {
    switch (this.reward.type) {
      case RewardOfferType.Diamond:
      case RewardOfferType.Token:
      case RewardOfferType.Bread:
      case RewardOfferType.AvatarItem:
      case RewardOfferType.ReferralRole:
      case RewardOfferType.MembershipTier:
      case RewardOfferType.ClassroomMembershipTierSet:
      case RewardOfferType.ClassroomPackage:
      case RewardOfferType.WebPlan:
      case RewardOfferType.WeaMembership:
        return DeliveryMethodType.InApp;
      case RewardOfferType.SubscriptionKey:
      case RewardOfferType.Coupon:
      case RewardOfferType.GiftCard:
        return DeliveryMethodType.Email;
      case RewardOfferType.PhysicalGood:
        return DeliveryMethodType.Mail;
      case RewardOfferType.Certificate:
        return DeliveryMethodType.Email;
    }
  }

  reachRedeemTimes(numOfRecords = 0) {
    return (
      this.maxRedemptionsPerUser !== null &&
      this.maxRedemptionsPerUser <= numOfRecords
    );
  }

  getOfferState(now = Date.now()) {
    if (this.startedAt.getTime() > now) {
      return OfferStateType.Scheduled;
    } else if ((this.endedAt?.getTime() ?? Infinity) > now) {
      return OfferStateType.Active;
    } else {
      return OfferStateType.Deactive;
    }
  }

  fulfillRequirement(
    scoreTotal: number | undefined,
    ranking:
      | {
          rankingType: AlbumRankingType;
          leaderboardId: string;
          rankTier: AlbumRankTier;
          band:
            | AlbumRankTier.Band1
            | AlbumRankTier.Band2
            | AlbumRankTier.Band3
            | AlbumRankTier.Others;
        }
      | undefined,
  ): boolean {
    if (this.requirement === null) {
      return true;
    }
    if ('classroomSummary' in this.requirement) {
      return (scoreTotal ?? 0) > this.requirement.classroomSummary.scoreTotal;
    }
    const selfLeaderboard = this.requirement.leaderboard;

    if (
      ranking === undefined ||
      selfLeaderboard.leaderboardId !== ranking.leaderboardId ||
      selfLeaderboard.rankingType !== ranking.rankingType
    ) {
      return false;
    }

    const tier: Record<AlbumRankTier, number> = {
      [AlbumRankTier.First]: -3,
      [AlbumRankTier.Second]: -2,
      [AlbumRankTier.Third]: -1,
      [AlbumRankTier.Top5]: 0,
      [AlbumRankTier.Top10]: 1,
      [AlbumRankTier.Top50]: 2,
      [AlbumRankTier.Top100]: 3,
      [AlbumRankTier.Band1]: 4,
      [AlbumRankTier.Band2]: 5,
      [AlbumRankTier.Band3]: 6,
      [AlbumRankTier.Others]: 7,
    };

    const myNTier = tier[selfLeaderboard.rankTier];

    if (myNTier < 0) {
      return tier[ranking.rankTier] === myNTier;
    }
    if (myNTier < 4) {
      return tier[ranking.rankTier] <= myNTier;
    }
    return tier[ranking.band] <= myNTier;
  }

  canBuy(numOfRecords = 0, now = Date.now()) {
    const ongoing =
      this.startedAt.getTime() < now &&
      (this.endedAt?.getTime() ?? Infinity) > now;
    return (
      ongoing && !this.reachRedeemTimes(numOfRecords) && !this.isOutOfStock
    );
  }
}
